// Shared building blocks for the Design & Engineering pages.
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { D, designError, designErrorList, fmtDateTime, fmtSize, label, openDesignFile, useDesignMe } from "@/lib/designApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CheckCircle2, Download, FileText, Lock, PenLine, Send, ShieldCheck, Trash2, Undo2, Upload, XCircle, Rocket, GitBranch,
  Ban, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  under_review: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  approved: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  released: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  superseded: "bg-slate-100 text-slate-500 line-through",
  obsolete: "bg-red-50 text-red-600",
  implemented: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  // requests / ECR / NPD
  new: "bg-blue-50 text-blue-700", assigned: "bg-indigo-50 text-indigo-700", in_progress: "bg-amber-50 text-amber-700",
  on_hold: "bg-orange-50 text-orange-700", completed: "bg-emerald-50 text-emerald-700", closed: "bg-slate-100 text-slate-600",
  rejected: "bg-red-50 text-red-600", open: "bg-blue-50 text-blue-700", converted: "bg-violet-50 text-violet-700",
  active: "bg-blue-50 text-blue-700", passed: "bg-emerald-50 text-emerald-700", conditional: "bg-lime-50 text-lime-700",
  failed: "bg-red-50 text-red-600", pending: "bg-slate-100 text-slate-500",
};

export function StatusBadge({ status, stage }) {
  const text = status === "under_review" && stage ? `in review · ${stage === "check" ? "to check" : "to approve"}` : label(status);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}
      data-testid={`status-${status}`}>
      {["released", "approved", "superseded", "obsolete", "under_review"].includes(status) && <Lock className="w-2.5 h-2.5" />}
      {text}
    </span>
  );
}

const PRIORITY_STYLE = { low: "text-slate-500", medium: "text-sky-700", high: "text-orange-600", urgent: "text-red-600 font-semibold" };
export function PriorityTag({ priority }) {
  return <span className={`text-xs uppercase tracking-wider ${PRIORITY_STYLE[priority] || ""}`}>{priority}</span>;
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3 max-md:items-start max-md:gap-2">
      <div className="max-md:min-w-0 max-md:w-full">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 max-md:text-[10px] max-md:tracking-[0.12em]">
          Design &amp; Engineering<span className="md:hidden"> <span className="text-slate-300">/</span> {title}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 max-md:text-xl max-md:font-bold max-md:mt-0.5" style={{ fontFamily: "Manrope" }}>{title}</h1>
        {subtitle && <div className="text-xs text-slate-500 mt-1 max-md:text-[11px] max-md:leading-snug max-md:line-clamp-2">{subtitle}</div>}
      </div>
      <div className="flex gap-2 flex-wrap max-md:[&_button]:h-8 max-md:[&_button]:px-3 max-md:[&_button]:text-xs">{children}</div>
    </div>
  );
}

export function Stat({ label: l, value, tone = "text-slate-900", hint, onClick, testid }) {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} data-testid={testid}
      className={`text-left bg-white border border-slate-200 rounded-lg p-4 max-md:p-2.5 max-md:rounded-md max-md:min-w-0 ${onClick ? "hover:border-blue-300 hover:shadow-sm transition max-md:active:bg-slate-50" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 max-md:text-[9.5px] max-md:tracking-[0.08em] max-md:leading-tight max-md:font-medium">{l}</div>
      <div className={`text-2xl font-extrabold mt-1 max-md:text-lg max-md:leading-tight max-md:mt-0.5 ${tone}`}>{value ?? "—"}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5 max-md:text-[10px] max-md:leading-tight max-md:truncate">{hint}</div>}
    </Cmp>
  );
}

export function NoAccess({ error }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="design-no-access">
      <ShieldCheck className="w-8 h-8 mx-auto text-slate-400" />
      <div className="mt-2 font-semibold text-slate-800">No access to Design &amp; Engineering</div>
      <div className="text-sm text-slate-500 mt-1">{error || "Ask an administrator to give you a design role."}</div>
    </div>
  );
}

export function ErrorList({ errors }) {
  if (!errors?.length) return null;
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 space-y-1" data-testid="design-error-list">
      {errors.map((e, i) => <div key={i} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{e}</div>)}
    </div>
  );
}

export function Section({ title, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 max-md:px-3 max-md:py-2 max-md:gap-2">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold max-md:tracking-[0.08em] max-md:text-[10.5px]">{title}</div>
          <div className="flex gap-2">{right}</div>
        </div>
      )}
      <div className="p-4 max-md:p-3">{children}</div>
    </div>
  );
}

export function Field({ label: l, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-xs text-slate-600">{l}</Label>
      {children}
    </div>
  );
}

export function SimpleSelect({ value, onChange, options, placeholder = "Select…", testid, disabled }) {
  return (
    <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const t = typeof o === "string" ? label(o) : o.label;
          return <SelectItem key={v} value={v}>{t}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// E-signature dialog: re-enter password to sign check / approve / release.
// ---------------------------------------------------------------------------
export function SignDialog({ open, onOpenChange, title, meaning, requireComment, requirePassword = true, onSubmit, children }) {
  const [password, setPassword] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  useEffect(() => { if (open) { setPassword(""); setComment(""); setErrors([]); } }, [open]);
  const submit = async () => {
    if (requireComment && !comment.trim()) { setErrors(["A comment is required."]); return; }
    setBusy(true);
    setErrors([]);
    try {
      await onSubmit({ password: password || undefined, comment });
      onOpenChange(false);
    } catch (e) {
      const list = designErrorList(e);
      setErrors(list.length ? list : [designError(e)]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="design-sign-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {meaning && <DialogDescription>{meaning}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {children}
          <Field label={requireComment ? "Comment (required)" : "Comment"}>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="design-sign-comment" />
          </Field>
          {requirePassword && (
            <Field label="Password (electronic signature)">
              <Input type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="design-sign-password"
                onKeyDown={(e) => e.key === "Enter" && submit()} />
            </Field>
          )}
          <ErrorList errors={errors} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} disabled={busy} data-testid="design-sign-submit">
            {busy ? "Signing…" : "Sign & confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Workflow bar for controlled records (and ECNs).
// ---------------------------------------------------------------------------
const ACTION_META = {
  submit: { text: "Submit for review", icon: Send, sign: false, meaning: "Send this draft to a Checker." },
  withdraw: { text: "Withdraw", icon: Undo2, sign: false, meaning: "Pull it back to draft for further edits." },
  check: { text: "Check ✓", icon: CheckCircle2, sign: true, meaning: "I have checked this record and it is correct." },
  approve: { text: "Approve", icon: ShieldCheck, sign: true, meaning: "I approve this record." },
  reject: { text: "Reject", icon: XCircle, sign: false, comment: true, meaning: "Return to the maker with comments." },
  release: { text: "Release", icon: Rocket, sign: true, meaning: "Release for use by Production, Purchase, Stores, QC and Sales. The record will be locked." },
};

export function WorkflowBar({ entity, record, onChanged, extra }) {
  const { me, can } = useDesignMe();
  const [act, setAct] = useState(null);
  const [revOpen, setRevOpen] = useState(false);
  if (!record || !me) return null;
  const st = record.status;
  const actions = [];
  if (st === "draft" && can("submit")) actions.push("submit");
  if (st === "under_review") {
    actions.push("withdraw");
    if (record.review_stage === "check" && can("check")) actions.push("check", "reject");
    if (record.review_stage === "approve" && can("approve")) actions.push("approve", "reject");
  }
  if (st === "approved" && can("release") && entity !== "ecns") actions.push("release");
  const esign = me.config?.esign_require_password !== false;

  const run = async ({ password, comment }) => {
    await api.post(`${D}/${entity}/${record.id}/workflow`, { action: act, password, comment });
    toast.success(`${ACTION_META[act].text.replace(" ✓", "")} done`);
    onChanged?.();
  };
  const runNoSign = async (a) => {
    try {
      await api.post(`${D}/${entity}/${record.id}/workflow`, { action: a });
      toast.success(`${ACTION_META[a].text} done`);
      onChanged?.();
    } catch (e) {
      toast.error(designError(e));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="design-workflow-bar">
      <StatusBadge status={st} stage={record.review_stage} />
      {record.locked && st !== "draft" && (
        <span className="text-[11px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
      )}
      <div className="flex-1" />
      {actions.map((a) => {
        const M = ACTION_META[a];
        const Icon = M.icon;
        const needsDialog = M.sign || M.comment;
        return (
          <Button key={a} size="sm" variant={a === "reject" || a === "withdraw" ? "outline" : "default"}
            className={a === "reject" || a === "withdraw" ? "" : "bg-blue-600 hover:bg-blue-700"}
            onClick={() => (needsDialog ? setAct(a) : runNoSign(a))} data-testid={`wf-${a}`}>
            <Icon className="w-3.5 h-3.5 mr-1" /> {M.text}
          </Button>
        );
      })}
      {st === "released" && entity !== "ecns" && can("create_edit") && (
        <Button size="sm" variant="outline" onClick={() => setRevOpen(true)} data-testid="wf-revise">
          <GitBranch className="w-3.5 h-3.5 mr-1" /> New revision (ECN)
        </Button>
      )}
      {extra}
      {act && (
        <SignDialog open={!!act} onOpenChange={(o) => !o && setAct(null)}
          title={`${ACTION_META[act].text.replace(" ✓", "")} — ${record.number}${record.revision ? ` Rev ${record.revision}` : ""}`}
          meaning={ACTION_META[act].meaning} requireComment={!!ACTION_META[act].comment}
          requirePassword={ACTION_META[act].sign && esign} onSubmit={run} />
      )}
      {revOpen && <ReviseDialog entity={entity} record={record} onClose={() => setRevOpen(false)} onDone={onChanged} />}
    </div>
  );
}

function ReviseDialog({ entity, record, onClose, onDone }) {
  const [ecns, setEcns] = useState([]);
  const [ecnId, setEcnId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`${D}/ecns`, { params: { status: "approved" } }).then((r) => {
      setEcns(r.data.filter((e) => (e.affected || []).some((a) => a.entity === entity && a.number === record.number && a.action === "revise")));
    }).catch(() => setEcns([]));
  }, [entity, record.number]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${D}/${entity}/${record.id}/revise`, { ecn_id: ecnId });
      toast.success(`Rev ${data.revision} created as draft`);
      onClose();
      onDone?.(data);
    } catch (e) {
      toast.error(designError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New revision of {record.number}</DialogTitle>
          <DialogDescription>Released records are locked. A new revision needs an approved ECN that lists this record.</DialogDescription>
        </DialogHeader>
        {ecns.length === 0 ? (
          <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
            No approved ECN covers {record.number}. Raise an ECR / ECN under <b>Change Control</b> first.
          </div>
        ) : (
          <Field label="Approved ECN">
            <SimpleSelect value={ecnId} onChange={setEcnId} testid="revise-ecn-select"
              options={ecns.map((e) => ({ value: e.id, label: `${e.number} — ${e.title}` }))} />
          </Field>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={!ecnId || busy} onClick={go} data-testid="revise-confirm">
            Create draft revision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Files panel (vault upload / stamped download / remove)
// ---------------------------------------------------------------------------
export function FilesPanel({ ownerType, ownerId, files, editable, onChanged, title = "Files" }) {
  const { me, can } = useDesignMe();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const accept = (me?.config?.allowed_extensions || []).map((e) => `.${e}`).join(",");
  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    const max = (me?.config?.max_upload_mb || 50) * 1024 * 1024;
    setBusy(true);
    try {
      for (const f of list) {
        if (f.size > max) { toast.error(`${f.name} exceeds ${me?.config?.max_upload_mb} MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`${D}/files/${ownerType}/${ownerId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      onChanged?.();
    } catch (err) {
      toast.error(designError(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    try {
      await api.delete(`${D}/files/${f.id}`);
      onChanged?.();
    } catch (err) {
      toast.error(designError(err));
    }
  };
  const open = async (f, save) => {
    try { await openDesignFile(f, { save }); } catch (err) { toast.error(designError(err, "Download not permitted")); }
  };
  return (
    <Section title={`${title} (${files?.length || 0})`} right={editable && (
      <>
        <input ref={ref} type="file" multiple className="hidden" accept={accept} onChange={upload} data-testid="design-file-input" />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="design-file-upload">
          <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Uploading…" : "Upload"}
        </Button>
      </>
    )}>
      {!files?.length ? (
        <div className="text-xs text-slate-500">No files. {editable ? `CAD / PDF / documents up to ${me?.config?.max_upload_mb || 50} MB.` : ""}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className="py-2 flex items-center gap-3 text-sm">
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <button className="text-blue-600 hover:underline truncate text-left" onClick={() => open(f, false)} data-testid={`file-open-${f.id}`}>
                {f.filename}
              </button>
              <span className="text-[11px] text-slate-500 whitespace-nowrap">{fmtSize(f.size)} · {f.uploaded_by_name || f.uploaded_by} · {fmtDateTime(f.uploaded_at)}</span>
              <div className="flex-1" />
              <button title="Download" className="text-slate-500 hover:text-blue-600" onClick={() => open(f, true)}><Download className="w-4 h-4" /></button>
              {editable && can("create_edit") && (
                <button title="Remove" className="text-slate-500 hover:text-red-600" onClick={() => remove(f)}><Trash2 className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] text-slate-400 mt-2">Downloads are permission-checked, logged in the audit trail, and PDFs are stamped with your name, time and the record status.</div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Revisions + signatures + audit history
// ---------------------------------------------------------------------------
export function HistoryPanel({ entity, record, onOpenRevision }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!record?.id) return;
    api.get(`${D}/${entity}/${record.id}/history`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [entity, record?.id, record?.status, record?.updated_at]);
  return (
    <div className="space-y-4">
      {record?.revisions?.length > 0 && (
        <Section title="Revisions">
          <div className="flex flex-wrap gap-2">
            {record.revisions.map((r) => (
              <button key={r.id} onClick={() => onOpenRevision?.(r.id)}
                className={`px-2.5 py-1 rounded border text-xs flex items-center gap-2 ${r.id === record.id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
                <b>Rev {r.revision}</b> <StatusBadge status={r.status} />
                {r.ecn_number && <span className="text-slate-500">{r.ecn_number}</span>}
              </button>
            ))}
          </div>
        </Section>
      )}
      <Section title="E-signatures">
        {!data?.signatures?.length ? <div className="text-xs text-slate-500">Not signed yet.</div> : (
          <table className="w-full text-xs">
            <tbody>
              {data.signatures.map((s) => (
                <tr key={s.id} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 font-semibold text-slate-800"><PenLine className="w-3 h-3 inline mr-1" />{s.meaning}</td>
                  <td className="py-1.5 pr-3">{s.user_name} <span className="text-slate-500">&lt;{s.user_email}&gt;</span></td>
                  <td className="py-1.5 pr-3 text-slate-500">{label(s.functional_role)}</td>
                  <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDateTime(s.at)}</td>
                  <td className="py-1.5 text-slate-600">{s.comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section title="Audit trail (all revisions)">
        {!data?.audit?.length ? <div className="text-xs text-slate-500">No entries.</div> : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <tbody>
                {data.audit.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50 align-top">
                    <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                    <td className="py-1.5 pr-3 font-medium">{label(a.action)}</td>
                    <td className="py-1.5 pr-3 text-slate-600 whitespace-nowrap">{a.ref}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{a.from_status || a.to_status ? `${label(a.from_status) || "—"} → ${label(a.to_status) || "—"}` : ""}</td>
                    <td className="py-1.5 pr-3">{a.user_name || a.user_email}</td>
                    <td className="py-1.5 text-slate-500 break-all">{summariseDetails(a.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function summariseDetails(d) {
  if (!d || typeof d !== "object") return "";
  if (d.changes) return Object.keys(d.changes).join(", ");
  if (d.filename) return d.filename;
  if (d.comment) return d.comment;
  return Object.entries(d).filter(([, v]) => v !== "" && v != null && typeof v !== "object").map(([k, v]) => `${k}: ${v}`).join(" · ");
}

export function ObsoleteButton({ entity, record, onChanged }) {
  const { can } = useDesignMe();
  const [open, setOpen] = useState(false);
  const [ecns, setEcns] = useState([]);
  const [ecnId, setEcnId] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get(`${D}/ecns`, { params: { status: "approved,implemented" } }).then((r) => setEcns(r.data.filter((e) =>
      (e.affected || []).some((a) => a.entity === entity && a.number === record.number && a.action === "obsolete")))).catch(() => {});
  }, [open, entity, record.number]);
  if (!can("release") || !["released", "approved"].includes(record.status)) return null;
  return (
    <>
      <Button size="sm" variant="outline" className="text-red-600" onClick={() => setOpen(true)} data-testid="wf-obsolete">
        <Ban className="w-3.5 h-3.5 mr-1" /> Obsolete
      </Button>
      {open && (
        <SignDialog open={open} onOpenChange={setOpen} title={`Obsolete ${record.number}`}
          meaning="Requires an approved ECN listing this record with action 'obsolete'."
          onSubmit={async ({ password, comment }) => {
            await api.post(`${D}/${entity}/${record.id}/obsolete`, { ecn_id: ecnId, password, comment });
            toast.success("Marked obsolete");
            onChanged?.();
          }}>
          {ecns.length ? (
            <Field label="ECN"><SimpleSelect value={ecnId} onChange={setEcnId} options={ecns.map((e) => ({ value: e.id, label: `${e.number} — ${e.title}` }))} /></Field>
          ) : <div className="text-sm text-amber-700">No approved ECN authorises obsoleting this record.</div>}
        </SignDialog>
      )}
    </>
  );
}

// Searchable part-number picker over design items (used by BOM / routing / docs / ECN forms).
export function ItemPicker({ value, onChange, placeholder = "Part number", testid, excludeNumber }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      api.get(`${D}/items`, { params: { q, limit: 30 } }).then((r) => setOpts(r.data.filter((i) => i.number !== excludeNumber && i.status !== "obsolete").slice(0, 15))).catch(() => setOpts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, excludeNumber]);
  return (
    <div className="relative">
      <Input value={q} placeholder={placeholder} data-testid={testid}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value.toUpperCase()); onChange(e.target.value.toUpperCase()); }} />
      {open && opts.length > 0 && (
        <div className="absolute z-50 mt-1 w-[28rem] max-w-[90vw] bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
          {opts.map((o) => (
            <button key={o.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); setQ(o.number); onChange(o.number, o); setOpen(false); }}>
              <span className="mono font-semibold">{o.number}</span>
              <span className="truncate text-slate-600">{o.name}</span>
              <span className="ml-auto"><StatusBadge status={o.status} /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function useQueryId() {
  const params = new URLSearchParams(window.location.search);
  return params.get("id");
}

// Phones only: keep the first column (the record number / name) pinned while
// the rest of a wide table scrolls sideways.
const STICKY_FIRST = "max-md:sticky max-md:left-0 max-md:z-[1] max-md:bg-white max-md:shadow-[1px_0_0_0_#f1f5f9]";

export function DataTable({ columns, rows, onRowClick, empty = "Nothing here yet.", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap max-md:text-[13px]" data-testid={testid}>
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-100">
              {columns.map((c, ci) => <th key={c.key} className={`py-2.5 px-3 max-md:py-2 max-md:px-2.5 ${ci === 0 ? STICKY_FIRST : ""} ${c.className || ""}`}>{c.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="py-10 text-center text-slate-500 text-sm">{empty}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id || i} onClick={() => onRowClick?.(r)}
                className={`border-b border-slate-50 ${onRowClick ? "cursor-pointer hover:bg-slate-50/80" : ""}`}>
                {columns.map((c, ci) => <td key={c.key} className={`py-2 px-3 max-md:py-2 max-md:px-2.5 ${ci === 0 ? STICKY_FIRST : ""} ${c.cellClass || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
